/**
 * useLineageTimeline — Time-travel selection model for run lineage.
 *
 * Cycle 069 Sprint 0: Establishes the state model for lineage
 * time-travel. Distinguishes live/latest position from a selected
 * historical position. Drives run detail, candidate lineage, and
 * attestation lineage inspection.
 *
 * Sprint 1 adds the scrubber UI on top of this model.
 * Sprint 2 connects it to canvas badge overlays.
 */

import { useState, useMemo, useCallback } from 'react';
import type { EnvironmentRun } from '../types/environmentRun';

// ── Types ────────────────────────────────────────────────────────────

export interface TimelinePoint {
  runId: string;
  index: number;
  timestamp: number;
  status: string;
  compositeScore: number | null;
  brierScore: number | null;
  isCertified: boolean;
}

export type TimelinePosition = 'live' | 'historical';

export interface LineageTimelineState {
  /** All timeline points (sorted newest first) */
  points: TimelinePoint[];
  /** Currently selected point (null = live/latest) */
  selectedPoint: TimelinePoint | null;
  /** Index of selected point in the points array (null = live) */
  selectedIndex: number | null;
  /** Whether we're viewing live state or a historical selection */
  position: TimelinePosition;
  /** The run being inspected (selected or latest) */
  inspectedRunId: string | null;

  // Actions
  selectPoint: (index: number) => void;
  selectRunId: (runId: string) => void;
  clearSelection: () => void;
  scrubTo: (index: number) => void;

  /** Whether there are any historical points to scrub through */
  hasHistory: boolean;
  /** Total number of timeline points */
  totalPoints: number;
}

// ── Hook ─────────────────────────────────────────────────────────────

export function useLineageTimeline(runs: EnvironmentRun[]): LineageTimelineState {
  // Store selected run by ID so it survives list refreshes/reorders
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const points = useMemo<TimelinePoint[]>(() => {
    return runs
      .map((run, idx) => ({
        runId: run.run_id,
        index: idx,
        timestamp: new Date(run.created_at).getTime(),
        status: run.status,
        compositeScore: run.composite_score,
        brierScore: run.brier_score,
        isCertified: !!run.certificate_id || !!run.attestation_id,
      }))
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [runs]);

  // Derive index from stored runId (stable across list refreshes)
  const selectedIndex = selectedRunId !== null
    ? points.findIndex((p) => p.runId === selectedRunId)
    : null;
  // If the selected run was removed from the list, clear selection
  const resolvedIndex = selectedIndex !== null && selectedIndex >= 0 ? selectedIndex : null;

  const selectedPoint = resolvedIndex !== null ? points[resolvedIndex] : null;
  const position: TimelinePosition = selectedPoint ? 'historical' : 'live';
  const inspectedRunId = selectedPoint?.runId ?? (points.length > 0 ? points[0].runId : null);

  const selectPoint = useCallback((index: number) => {
    if (index >= 0 && index < points.length) {
      setSelectedRunId(points[index].runId);
    }
  }, [points]);

  const selectRunIdAction = useCallback((runId: string) => {
    setSelectedRunId(runId);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedRunId(null);
  }, []);

  const scrubTo = useCallback((index: number) => {
    if (index >= 0 && index < points.length) {
      setSelectedRunId(points[index].runId);
    }
  }, [points]);

  return {
    points,
    selectedPoint,
    selectedIndex: resolvedIndex,
    position,
    inspectedRunId,
    selectPoint,
    selectRunId: selectRunIdAction,
    clearSelection,
    scrubTo,
    hasHistory: points.length > 1,
    totalPoints: points.length,
  };
}
